import useSWR from 'swr';

import myShopClient from '@services/nearlive';

/**
 * Orders for the current shop. `from` / `to` are ISO date strings.
 * Pages compose these with their own filters (awaiting / fulfilled / etc).
 *
 * Calls `/api/orders/{shopId}` directly rather than going through
 * `@services/orders` — that module pulls in `dashboard-shared`'s
 * `ShopProvider` → `LoadingContainer` → `@nearst/ui`, which side-effect-loads
 * a stack of global SCSS (Navigation/style.scss paints a grey background
 * behind every `<header>`, global.scss overrides heading fonts, etc.).
 * Mirrors the pattern used by `useChannels` / `useInsights` / `useInventory`.
 *
 * Returns `{ orders, error, isLoading, mutate, updateOrder, refundOrder }`.
 * `updateOrder` POSTs the full mutated order (including `emailMessage` /
 * `skipEmail` when the email customisation modal is involved) and patches the
 * SWR cache optimistically, mirroring `dashboard-shared/services/orders.js:update`.
 * `refundOrder` requests a partial refund for specific line quantities; the
 * refund itself executes asynchronously via a stream handler, so the response
 * order carries `refundPending: 'true'` and we revalidate shortly after.
 */
export function useOrders(shopId, { from, to } = {}) {
	const key = shopId ? ['unify:orders', shopId, from, to] : null;
	const { data, error, isLoading, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/orders/${shopId}`, { params: { start: from, end: to } });
			return data;
		},
		{ revalidateOnFocus: false, refreshInterval: 60_000 }
	);

	const updateOrder = async (newOrder) => {
		const client = await myShopClient();
		const targetShopId = newOrder.pk ? newOrder.pk.replace('shop#', '') : shopId;
		const { data: updated } = await client.post(`/api/orders/${targetShopId}`, newOrder);
		const next = updated || newOrder;
		await mutate(
			(prev) => {
				if (!Array.isArray(prev)) return prev;
				return prev.map((o) => (o.id === next.id ? { ...o, ...next } : o));
			},
			{ revalidate: false }
		);
		return next;
	};

	const refundOrder = async (order, items) => {
		const client = await myShopClient();
		const targetShopId = order.pk ? order.pk.replace('shop#', '') : shopId;
		const { data: response } = await client.post(`/api/orders/${targetShopId}/refund`, { id: order.id, items });
		// The endpoint returns the updated order directly (same shape as updateOrder)
		const next = response?.id ? response : { ...order, refundPending: 'true' };
		await mutate(
			(prev) => {
				if (!Array.isArray(prev)) return prev;
				return prev.map((o) => (o.id === next.id ? { ...o, ...next } : o));
			},
			{ revalidate: false }
		);
		// The refund executes asynchronously via a stream handler — revalidate
		// after a short delay so the processed order state comes through.
		setTimeout(() => mutate(), 5000);
		return next;
	};

	return { orders: Array.isArray(data) ? data : [], error, isLoading, mutate, updateOrder, refundOrder };
}
